import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { BRANCHES, GRADE_LEVELS } from '@/mocks/mockData';
import AddTeacherModal from './components/AddTeacherModal';
import type { Teacher } from '@/mocks/mockData';

interface OgretmenRow {
  id: string;
  name: string;
  phone: string;
  email: string;
  branches: string[];
  gradeLevels: string[];
  canOnline: boolean;
  canFaceToFace: boolean;
  activeStudents: number;
  lessonsThisMonth: number;
  earningsThisMonth: number;
  pendingEarnings: number;
  status: 'Aktif' | 'Pasif';
  commissionRate: number;
  defaultRate: number;
  availability: string;
  note: string;
  geciciSifre: string | null;
}

interface EditForm {
  name: string; phone: string; email: string; branches: string[];
  gradeLevels: string[]; canOnline: boolean; canFaceToFace: boolean;
  availability: string; defaultRate: number; commissionRate: number;
  status: 'Aktif' | 'Pasif'; note: string;
}

export default function OgretmenlerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<OgretmenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<OgretmenRow | null>(null);
  const [deleteItem, setDeleteItem] = useState<OgretmenRow | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState<Array<{ ad_soyad: string; email: string; sifre: string; status: string; error?: string }> | null>(null);
  const [accountInfo, setAccountInfo] = useState<{ name: string; email: string; sifre: string } | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

    const [ogretmenRes, ogrenciRes, dersRes, odemeRes] = await Promise.all([
      supabase.from('egtpanel_ogretmenler').select('*').order('olusturma_tarihi', { ascending: false }),
      supabase.from('egtpanel_ogrenciler').select('id, atanan_ogretmen_id, aktif'),
      supabase.from('egtpanel_aktif_dersler').select('id, ogretmen_id, ders_tarihi').gte('ders_tarihi', monthStart).lte('ders_tarihi', monthEnd),
      supabase.from('egtpanel_odemeler').select('id, ogretmen_id, tutar, hakedis_orani, yonetici_onay').gte('dekont_tarihi', monthStart).lte('dekont_tarihi', monthEnd),
    ]);

    const activeStudentMap: Record<string, number> = {};
    (ogrenciRes.data ?? []).forEach((o: { atanan_ogretmen_id: string | null; aktif: boolean }) => {
      if (o.atanan_ogretmen_id && o.aktif) activeStudentMap[o.atanan_ogretmen_id] = (activeStudentMap[o.atanan_ogretmen_id] ?? 0) + 1;
    });
    const lessonMap: Record<string, number> = {};
    (dersRes.data ?? []).forEach((d: { ogretmen_id: string }) => { lessonMap[d.ogretmen_id] = (lessonMap[d.ogretmen_id] ?? 0) + 1; });
    const earningsMap: Record<string, number> = {};
    const pendingMap: Record<string, number> = {};
    (odemeRes.data ?? []).forEach((p: { ogretmen_id: string; tutar: number; hakedis_orani: number; yonetici_onay: boolean }) => {
      const share = (Number(p.tutar) * Number(p.hakedis_orani)) / 100;
      if (p.yonetici_onay) earningsMap[p.ogretmen_id] = (earningsMap[p.ogretmen_id] ?? 0) + share;
      else pendingMap[p.ogretmen_id] = (pendingMap[p.ogretmen_id] ?? 0) + share;
    });

    if (ogretmenRes.data) {
      setData(ogretmenRes.data.map((t) => ({
        id: t.id,
        name: t.ad_soyad,
        phone: t.telefon ?? '',
        email: t.email ?? '',
        branches: Array.isArray(t.branslar) ? t.branslar : [],
        gradeLevels: Array.isArray(t.sinif_seviyeleri) ? t.sinif_seviyeleri : [],
        canOnline: t.online_ders,
        canFaceToFace: t.yuz_yuze_ders,
        activeStudents: activeStudentMap[t.id] ?? 0,
        lessonsThisMonth: lessonMap[t.id] ?? 0,
        earningsThisMonth: earningsMap[t.id] ?? 0,
        pendingEarnings: pendingMap[t.id] ?? 0,
        status: t.aktif ? 'Aktif' : 'Pasif',
        commissionRate: Number(t.hakedis_orani ?? 50),
        defaultRate: Number(t.varsayilan_ders_ucreti ?? 0),
        availability: t.musaitlik_notu ?? '',
        note: t.aciklama ?? '',
        geciciSifre: t.gecici_sifre ?? null,
      })));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter((t) => {
    const q = search.toLowerCase();
    const matchQ = !q || t.name.toLowerCase().includes(q) || t.phone.includes(q);
    const matchStatus = !filterStatus || t.status === filterStatus;
    return matchQ && matchStatus;
  });

  const openEdit = (t: OgretmenRow) => {
    setEditItem(t);
    setEditForm({
      name: t.name, phone: t.phone, email: t.email,
      branches: t.branches, gradeLevels: t.gradeLevels,
      canOnline: t.canOnline, canFaceToFace: t.canFaceToFace,
      availability: t.availability, defaultRate: t.defaultRate,
      commissionRate: t.commissionRate,
      status: t.status, note: t.note,
    });
  };

  const toggleBranch = (b: string) => setEditForm((p) => p ? ({ ...p, branches: p.branches.includes(b) ? p.branches.filter((x) => x !== b) : [...p.branches, b] }) : p);
  const toggleGrade = (g: string) => setEditForm((p) => p ? ({ ...p, gradeLevels: p.gradeLevels.includes(g) ? p.gradeLevels.filter((x) => x !== g) : [...p.gradeLevels, g] }) : p);

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem || !editForm) return;
    setSaving(true);
    const { error } = await supabase.from('egtpanel_ogretmenler').update({
      ad_soyad: editForm.name,
      telefon: editForm.phone || null,
      email: editForm.email || null,
      branslar: editForm.branches,
      sinif_seviyeleri: editForm.gradeLevels,
      online_ders: editForm.canOnline,
      yuz_yuze_ders: editForm.canFaceToFace,
      musaitlik_notu: editForm.availability || null,
      varsayilan_ders_ucreti: editForm.defaultRate,
      hakedis_orani: editForm.commissionRate,
      aktif: editForm.status === 'Aktif',
      aciklama: editForm.note || null,
      guncelleme_tarihi: new Date().toISOString(),
    }).eq('id', editItem.id);
    setSaving(false);
    if (error) { showToast('Hata: Güncellenemedi.'); return; }
    showToast('Öğretmen güncellendi!');
    setEditItem(null);
    setEditForm(null);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    const { error } = await supabase.from('egtpanel_ogretmenler').delete().eq('id', deleteItem.id);
    setSaving(false);
    if (error) { showToast('Hata: Silinemedi.'); return; }
    showToast('Öğretmen silindi.');
    setDeleteItem(null);
    fetchData();
  };

  const handleAdd = async (_teacher: Teacher) => {
    showToast('Öğretmen eklendi!');
    setShowModal(false);
    await fetchData();
  };

  const handleBulkCreate = async () => {
    if (!window.confirm('Eksik hesabı olan öğretmenler için otomatik giriş hesabı oluşturulacak. Devam edilsin mi?')) return;
    setBulkLoading(true);
    setBulkResults(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        showToast('Oturumunuz geçersiz.');
        setBulkLoading(false);
        return;
      }
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/bulk-create-teacher-accounts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        }
      );
      const result = await response.json();
      if (!response.ok) {
        showToast(result.error || 'Hata oluştu.');
      } else {
        setBulkResults(result.created || []);
        showToast(result.message || 'İşlem tamamlandı.');
        fetchData();
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Beklenmeyen hata');
    } finally {
      setBulkLoading(false);
    }
  };

  const missingAccountCount = data.filter((t) => !t.email).length;

  return (
    <div className="p-6 space-y-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Öğretmenler</h1>
          <p className="text-slate-500 text-sm mt-1">Öğretmen kadrosu ve performans yönetimi</p>
        </div>
        <div className="flex items-center gap-2">
          {missingAccountCount > 0 && (
            <button
              onClick={handleBulkCreate}
              disabled={bulkLoading}
              className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              <i className="ri-user-add-line" />
              {bulkLoading ? 'Oluşturuluyor...' : `Eksik Hesapları Oluştur (${missingAccountCount})`}
            </button>
          )}
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#162d4a] transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-add-line" /> Öğretmen Ekle
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam', val: data.length, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Aktif', val: data.filter((t) => t.status === 'Aktif').length, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Aktif Öğrenci', val: data.reduce((a, t) => a + t.activeStudents, 0), color: 'text-indigo-700', bg: 'bg-indigo-50' },
          { label: 'Bu Ay Ders', val: data.reduce((a, t) => a + t.lessonsThisMonth, 0), color: 'text-amber-700', bg: 'bg-amber-50' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Öğretmen adı veya telefon..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Durumlar</option>
            <option>Aktif</option>
            <option>Pasif</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Öğretmen', 'Branşlar', 'Online/Yüz Yüze', 'Aktif Öğrenci', 'Bu Ay Ders', 'Bu Ay Kazanç', 'Bekleyen', 'Durum', 'Hesap', 'İşlemler'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-16">
                    <i className="ri-user-search-line text-4xl text-slate-200 block mb-2" />
                    <p className="text-slate-400 text-sm">{data.length === 0 ? 'Henüz öğretmen eklenmemiş' : 'Öğretmen bulunamadı'}</p>
                  </td></tr>
                ) : filtered.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1e3a5f] text-white text-sm font-bold shrink-0">{t.name.charAt(0)}</div>
                        <div>
                          <div className="font-medium text-slate-800">{t.name}</div>
                          <div className="text-xs text-slate-400">{t.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {t.branches.slice(0, 2).map((b) => <span key={b} className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-xs whitespace-nowrap">{b}</span>)}
                        {t.branches.length > 2 && <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-xs">+{t.branches.length - 2}</span>}
                        {t.branches.length === 0 && <span className="text-slate-400 text-xs">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex gap-1">
                        {t.canOnline && <span className="bg-cyan-50 text-cyan-700 px-1.5 py-0.5 rounded text-xs">Online</span>}
                        {t.canFaceToFace && <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-xs">Yüz yüze</span>}
                        {!t.canOnline && !t.canFaceToFace && <span className="text-slate-400 text-xs">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">{t.activeStudents}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{t.lessonsThisMonth}</td>
                    <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">{t.earningsThisMonth > 0 ? `₺${t.earningsThisMonth.toLocaleString('tr-TR')}` : '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={t.pendingEarnings > 0 ? 'text-orange-600 font-medium' : 'text-slate-500'}>
                        {t.pendingEarnings > 0 ? `₺${t.pendingEarnings.toLocaleString('tr-TR')}` : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${t.status === 'Aktif' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {t.geciciSifre ? (
                        <button
                          onClick={() => setAccountInfo({ name: t.name, email: t.email, sifre: t.geciciSifre! })}
                          className="text-xs font-medium px-2 py-1 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors cursor-pointer whitespace-nowrap"
                          title="Hesap bilgilerini görüntüle"
                        >
                          <i className="ri-key-line mr-1" />Bilgi
                        </button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => navigate(`/admin/ogretmenler/${t.id}`)} className="text-[#1e3a5f] text-xs font-medium px-2.5 py-1.5 rounded-lg border border-[#1e3a5f]/20 hover:bg-[#1e3a5f]/5 transition-colors cursor-pointer whitespace-nowrap">
                          Detay
                        </button>
                        <button onClick={() => openEdit(t)} className="text-slate-600 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-edit-line" />
                        </button>
                        <button onClick={() => setDeleteItem(t)} className="text-red-500 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Account Info Modal */}
      {accountInfo && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-800">Hesap Bilgisi</h2>
              <button onClick={() => setAccountInfo(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <p className="text-xs text-slate-400 font-medium mb-0.5">Öğretmen</p>
                <p className="text-sm font-semibold text-slate-800">{accountInfo.name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium mb-0.5">E-posta</p>
                <p className="text-sm font-mono text-slate-700 break-all">{accountInfo.email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium mb-0.5">Giriş Şifresi</p>
                <p className="text-sm font-mono font-semibold text-slate-800">{accountInfo.sifre}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-700">
                <i className="ri-information-line mr-1" />
                Öğretmen giriş yapıp şifresini değiştirdiğinde bu şifre geçersiz olur. Şifre sıfırlama için öğretmenin detay sayfasını kullanın.
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`E-posta: ${accountInfo.email}\nŞifre: ${accountInfo.sifre}`);
                  showToast('Hesap bilgisi kopyalandı!');
                }}
                className="w-full py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] transition-colors cursor-pointer"
              >
                <i className="ri-clipboard-line mr-1" /> Kopyala
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Create Results Modal */}
      {bulkResults && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Toplu Hesap Oluşturma Sonuçları</h2>
              <button onClick={() => setBulkResults(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-500 mb-4">
                Aşağıdaki e-posta ve şifre bilgilerini öğretmenlerle güvenli bir şekilde paylaşın.
              </p>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Öğretmen</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">E-posta</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Şifre</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Durum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {bulkResults.map((r, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{r.ad_soyad}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap font-mono text-xs">{r.email}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap font-mono text-xs">{r.sifre}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {r.status === 'Başarılı' ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Başarılı</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" title={r.error}>{r.status}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    const text = bulkResults.map((r) => `${r.ad_soyad}\nE-posta: ${r.email}\nŞifre: ${r.sifre}\nDurum: ${r.status}${r.error ? ' (' + r.error + ')' : ''}`).join('\n\n');
                    navigator.clipboard.writeText(text);
                    showToast('Tüm bilgiler kopyalandı!');
                  }}
                  className="flex-1 py-2.5 rounded-lg border border-[#1e3a5f]/20 text-[#1e3a5f] text-sm font-medium hover:bg-[#1e3a5f]/5 cursor-pointer"
                >
                  <i className="ri-clipboard-line mr-1" /> Tümünü Kopyala
                </button>
                <button onClick={() => setBulkResults(null)} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] cursor-pointer">
                  Tamam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showModal && <AddTeacherModal onClose={() => setShowModal(false)} onAdd={handleAdd} />}

      {/* Edit Modal */}
      {editItem && editForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Öğretmeni Düzenle</h2>
              <button onClick={() => { setEditItem(null); setEditForm(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ad Soyad *</label>
                  <input required value={editForm.name} onChange={(e) => setEditForm((p) => p ? ({ ...p, name: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Telefon</label>
                  <input value={editForm.phone} onChange={(e) => setEditForm((p) => p ? ({ ...p, phone: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">E-posta</label>
                  <input type="email" value={editForm.email} onChange={(e) => setEditForm((p) => p ? ({ ...p, email: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Varsayılan Ders Ücreti (₺)</label>
                  <input type="number" value={editForm.defaultRate} onChange={(e) => setEditForm((p) => p ? ({ ...p, defaultRate: Number(e.target.value) }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Hak Ediş Oranı (%)</label>
                  <input type="number" min={0} max={100} value={editForm.commissionRate} onChange={(e) => setEditForm((p) => p ? ({ ...p, commissionRate: Number(e.target.value) }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
                  <select value={editForm.status} onChange={(e) => setEditForm((p) => p ? ({ ...p, status: e.target.value as 'Aktif' | 'Pasif' }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option>Aktif</option>
                    <option>Pasif</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Ders Tipi</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editForm.canOnline} onChange={(e) => setEditForm((p) => p ? ({ ...p, canOnline: e.target.checked }) : p)} className="rounded" />
                    <span className="text-sm text-slate-700">Online ders</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editForm.canFaceToFace} onChange={(e) => setEditForm((p) => p ? ({ ...p, canFaceToFace: e.target.checked }) : p)} className="rounded" />
                    <span className="text-sm text-slate-700">Yüz yüze ders</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Branşlar</label>
                <div className="flex flex-wrap gap-2">
                  {BRANCHES.map((b) => (
                    <button key={b} type="button" onClick={() => toggleBranch(b)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${editForm.branches.includes(b) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1e3a5f]/40'}`}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Sınıf Seviyeleri</label>
                <div className="flex flex-wrap gap-2">
                  {GRADE_LEVELS.map((g) => (
                    <button key={g} type="button" onClick={() => toggleGrade(g)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${editForm.gradeLevels.includes(g) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400'}`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Müsaitlik Notu</label>
                <input value={editForm.availability} onChange={(e) => setEditForm((p) => p ? ({ ...p, availability: e.target.value }) : p)} placeholder="örn: Haftaiçi 14:00-22:00" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Açıklama Notu</label>
                <textarea value={editForm.note} onChange={(e) => setEditForm((p) => p ? ({ ...p, note: e.target.value }) : p)} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setEditItem(null); setEditForm(null); }} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-60 cursor-pointer">
                  {saving ? 'Kaydediliyor...' : 'Güncelle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">Öğretmeni Sil</h3>
            <p className="text-slate-500 text-sm mb-6">
              <strong>{deleteItem.name}</strong> adlı öğretmeni silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteItem(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
              <button onClick={handleDelete} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-60 cursor-pointer">
                {saving ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}